import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  BeakerIcon,
  BugAntIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import type { ComponentType } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { useFields } from '../lib/fields'
import { Shell } from '../pages/shell'
import { flowFor } from './flows/registry'
import { fieldIdsOf, str } from './flow'
import type { LogDraft } from './drafts-api'
import { problemMessage } from './messages'
import { useLogDrafts } from './use-log-drafts'

type Choice = { id: string; label: string; description: string; icon: ComponentType<{ className?: string }> } & (
  | { kind: string }
  | { group: string }
)

/** Top level: what did you do? A group opens a second list. */
const TOP: Choice[] = [
  {
    id: 'crop_inputs',
    kind: 'crop_inputs',
    label: 'Applied crop inputs',
    description: 'Sprays, fertilizer, tank mixes and fertigation',
    icon: BeakerIcon,
  },
  { id: 'amendment', kind: 'amendment', label: 'Soil amendment', description: 'Manure, compost, lime, mulch or row cover', icon: CircleStackIcon },
  { id: 'harvest', kind: 'harvest', label: 'Harvested', description: 'What came off a field, and how much', icon: CubeIcon },
  { id: 'scouting', kind: 'scouting', label: 'Scouted', description: 'Pests, disease, waterlogging, dying plants', icon: MagnifyingGlassIcon },
  { id: 'field_task', kind: 'field_task', label: 'Field work', description: 'Pruning, irrigation, mowing, mulching, hives', icon: ScissorsIcon },
  { id: 'equipment', kind: 'equipment', label: 'Equipment work', description: 'Cleaning, maintenance, calibration or repair', icon: WrenchScrewdriverIcon },
  { id: 'after', group: 'after', label: 'Packed, stored or shipped', description: 'Packing, storage and loads going out', icon: TruckIcon },
  { id: 'more', group: 'more', label: 'Facility and people', description: 'Building checks, pests, visitors, training', icon: BuildingOffice2Icon },
]

const GROUPS: Record<string, { title: string; choices: Choice[] }> = {
  after: {
    title: 'What happened to the product?',
    choices: [
      { id: 'packing', kind: 'packing', label: 'Packed or put into storage', description: 'Packed, repacked or stored, with lot codes', icon: ArchiveBoxIcon },
      { id: 'shipment', kind: 'shipment', label: 'Shipped out', description: 'A load to a packer or customer, and the truck check', icon: TruckIcon },
    ],
  },
  more: {
    title: 'What did you do?',
    choices: [
      { id: 'building_inspection', kind: 'building_inspection', label: 'Building inspection', description: 'A walk-through of a building or storage', icon: BuildingOffice2Icon },
      { id: 'hygiene', kind: 'hygiene', label: 'Hygiene facility check', description: 'Washrooms and hand-wash stations', icon: ShieldCheckIcon },
      { id: 'pest_check', kind: 'pest_check', label: 'Pest check', description: 'Traps and bait stations', icon: BugAntIcon },
      { id: 'visitor', kind: 'visitor', label: 'Visitor', description: 'Someone came onto the farm', icon: UserIcon },
      { id: 'training', kind: 'training', label: 'Training', description: 'A session with your crew', icon: AcademicCapIcon },
    ],
  },
}

/** A draft's fields, briefly: "Field A", "Field A, Field B" or "Field A, Field B +2". */
function fieldNames(names: (string | undefined)[]): string | undefined {
  const known = names.filter((n): n is string => !!n)
  if (known.length === 0) return undefined
  return known.length <= 2 ? known.join(', ') : `${known.slice(0, 2).join(', ')} +${known.length - 2}`
}

/** A choice is offered once its flow exists (or, for a group, once any of its choices does). */
const available = (c: Choice): boolean => ('kind' in c ? !!flowFor(c.kind) : GROUPS[c.group]!.choices.some(available))

function ago(iso: string) {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.round(hours / 24)} d ago`
}

/** One choice: a large row with its icon, what it covers, and a chevron (it opens a flow or a sub-list). */
function ChoiceRow({ choice, onClick }: { choice: Choice; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex min-h-18 w-full items-center gap-4 px-4 py-3 text-left hover:bg-zinc-50 focus-visible:bg-zinc-50 focus-visible:outline-none dark:hover:bg-white/5 dark:focus-visible:bg-white/5"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10 ring-inset dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/20">
          <choice.icon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base/6 font-semibold text-zinc-950 dark:text-white">{choice.label}</span>
          <span className="block text-sm/5 text-zinc-500 dark:text-zinc-400">{choice.description}</span>
        </span>
        <ChevronRightIcon className="size-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
      </button>
    </li>
  )
}

/**
 * `place` is what the entry is about: the field when one farm is open, otherwise the farm.
 * Delete asks in place: the button turns into "Yes, delete this entry" (no dialog), and turns back
 * after a few seconds or when focus leaves it, so a stray tap never deletes anything.
 */
function DraftRow({ draft, place, onDiscard }: { draft: LogDraft; place: string | undefined; onDiscard: () => Promise<void> }) {
  const flow = flowFor(draft.kind)
  const total = flow ? flow.steps.length + 2 : undefined
  const stale = Date.now() - new Date(draft.updatedAt).getTime() > 30 * 86_400_000
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!confirming || deleting) return
    const timer = setTimeout(() => setConfirming(false), 5000)
    return () => clearTimeout(timer)
  }, [confirming, deleting])

  async function handleDelete() {
    if (!confirming) {
      setError(null)
      setConfirming(true)
      return
    }
    setDeleting(true)
    try {
      await onDiscard()
    } catch (err) {
      setError(problemMessage(err, 'delete this entry'))
      setConfirming(false)
      setDeleting(false)
    }
  }
  return (
    <li className="flex flex-col gap-3 rounded-xl bg-white p-4 ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10">
      <div>
        <p className="font-semibold text-zinc-950 dark:text-white">
          {[flow?.label ?? draft.kind, place, str(draft.payload.date)].filter(Boolean).join(' · ')}
        </p>
        {error ? (
          <Text className="text-red-600 dark:text-red-400">{error}</Text>
        ) : confirming ? (
          <Text>What you filled in so far is deleted. Nothing is added to your records.</Text>
        ) : (
          <Text>
            {total ? `Step ${draft.step + 1} of ${total} · ` : ''}
            {ago(draft.updatedAt)}
            {stale ? ' · old' : ''}
          </Text>
        )}
      </div>
      <div className="flex gap-3">
        {flow && <Button href={`/farms/${draft.farmId}/log/${draft.kind}/${draft.id}`}>Continue</Button>}
        {confirming ? (
          <Button color="red" onClick={handleDelete} onBlur={() => !deleting && setConfirming(false)} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Yes, delete this entry'}
          </Button>
        ) : (
          <Button plain onClick={handleDelete}>
            Delete
          </Button>
        )}
      </div>
    </li>
  )
}

/**
 * "What did you do?": the activities as large rows; a group opens its own list, with a way back.
 * `exclude` leaves one out (the entry just made, on its Done screen).
 */
export function ActivityChoices({ onChoose, heading, exclude }: { onChoose: (kind: string) => void; heading: string; exclude?: string }) {
  const [group, setGroup] = useState<string | null>(null)
  const choices = (group ? GROUPS[group]!.choices : TOP).filter(available).filter((c) => !('kind' in c) || c.kind !== exclude)
  return (
    <section className="space-y-4">
      {group && (
        <button type="button" onClick={() => setGroup(null)} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
          <ChevronLeftIcon className="size-4" />
          Back
        </button>
      )}
      <Heading>{group ? GROUPS[group]!.title : heading}</Heading>
      <ul className="divide-y divide-zinc-950/5 overflow-hidden rounded-xl bg-white text-left ring-1 ring-zinc-950/10 dark:divide-white/10 dark:bg-white/5 dark:ring-white/10">
        {choices.map((c) => (
          <ChoiceRow key={c.id} choice={c} onClick={() => ('kind' in c ? onChoose(c.kind) : setGroup(c.group))} />
        ))}
      </ul>
    </section>
  )
}

/**
 * `/log` (every farm) and `/farms/:farmId/log` (one farm): pick up an unfinished entry, or say what
 * you did. With no farm in the address, the wizard asks which farm and field on its first step.
 */
export function LogStartPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { farms, getFarm } = useFarms()
  const { fields } = useFields(org?.id, farmId)
  const { drafts, discard } = useLogDrafts(org?.id, farmId ? [farmId] : farms.map((f) => f.id))
  const logBase = farmId ? `/farms/${farmId}/log` : '/log'
  const [group, setGroup] = useState<string | null>(null)
  if (!org) return null

  const fieldQuery = search.get('field') ? `?field=${search.get('field')}` : ''
  const choose = (c: Choice) => ('kind' in c ? navigate(`${logBase}/${c.kind}${fieldQuery}`) : setGroup(c.group))
  const choices = (group ? GROUPS[group]!.choices : TOP).filter(available)

  return (
    <Shell>
      <div className="mx-auto max-w-xl space-y-8">
        {group ? (
          <button type="button" onClick={() => setGroup(null)} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
            <ChevronLeftIcon className="size-4" />
            Back
          </button>
        ) : (
          <Link href={farmId ? `/farms/${farmId}` : '/'} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
            <ChevronLeftIcon className="size-4" />
            {farmId ? (getFarm(farmId)?.name ?? 'Farm') : 'Farms'}
          </Link>
        )}

        {!group && drafts.length > 0 && (
          <section className="space-y-3">
            <Subheading>Pick up where you left off</Subheading>
            <ul className="space-y-3">
              {drafts.map((d) => (
                <DraftRow
                  key={d.id}
                  draft={d}
                  place={farmId ? fieldNames(fieldIdsOf(d.payload).map((id) => fields.find((f) => f.id === id)?.name)) : getFarm(d.farmId)?.name}
                  onDiscard={() => discard(d)}
                />
              ))}
            </ul>
          </section>
        )}

        <section className="space-y-4">
          <Heading>{group ? GROUPS[group]!.title : 'What did you do today?'}</Heading>
          <ul className="divide-y divide-zinc-950/5 overflow-hidden rounded-xl bg-white ring-1 ring-zinc-950/10 dark:divide-white/10 dark:bg-white/5 dark:ring-white/10">
            {choices.map((c) => (
              <ChoiceRow key={c.id} choice={c} onClick={() => choose(c)} />
            ))}
          </ul>
        </section>
      </div>
    </Shell>
  )
}
